import * as Yup from 'yup';
import { Field, FieldArray, ErrorMessage, Formik } from 'formik';
import Swal from 'sweetalert2';
import { swlAlertMapping } from '../../../../component/sweetAlert';
import { addRedem, getUserTypeAll, redemPartnerUserList } from '../../../api';
import { useContext, useEffect, useState } from 'react';
import { ComponentRequest } from '../../../../component/componentLayout';
import clsx from 'clsx';
import { FullPageLoader } from '../../../../component/helper';

/**
 * Validation schema for the RedemLabel form using Yup.
 * Ensures required fields for mapping, TDS, and logistic value.
 */
const editUserSchema = Yup.object().shape({
    mapping: Yup.array().of(
        Yup.object().shape({
            point: Yup.number().required('Points are required'),
            special_point: Yup.boolean(),
            offer_point: Yup.number().when('special_point', {
                is: true,
                then: Yup.number().required('Offer Point is required'),
                otherwise: Yup.number().nullable(),
            }),
            from_date: Yup.string().when('special_point', {
                is: true,
                then: Yup.string().required('From Date is required'),
                otherwise: Yup.string().nullable(),
            }),
            to_date: Yup.string().when('special_point', {
                is: true,
                then: Yup.string().required('To Date is required'),
                otherwise: Yup.string().nullable(),
            }),
        })
    ),
    sku_tds: Yup.string().required('TDS is required'),
    logistic_value: Yup.string().required('Logistic value is required'),
    is_free_product: Yup.string().oneOf(['yes', 'no']).required('Free product is required'),
});



/**
 * RightRedemLabel component for rendering the add redemption label button and drawer.
 * Handles drawer state and passes selected row to RedemDrawer.
 */
const RightRedemLabel = ({ row }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    /**
     * Toggles the drawer and sets the selected row for editing.
     */
    const handleToggleDrawer = (currentRow) => {
        setSelectedRow(currentRow);
        setIsDrawerOpen(true);
    };

    return (
        <>
            <span
                className="symbol symbol-25px cursor-pointer symbol-circle me-2"
                onClick={() => handleToggleDrawer(row)} // Pass the clicked row
            >
                <span className="symbol-label fs-6 fw-semibold text-white bg-danger">
                    <i className="fa fa-plus text-white fs-4 fw-semibold"></i>
                </span>
            </span>

            {isDrawerOpen && (
                <RedemDrawer
                    isDrawerOpen={isDrawerOpen}
                    setIsDrawerOpen={setIsDrawerOpen}
                    redemItem={selectedRow} // Pass the selected row
                />
            )}
        </>
    );
};


/**
 * RedemDrawer component for editing redemption mapping details in a drawer UI.
 * Handles form submission, user type fetching, and drawer state.
 */
const RedemDrawer = ({ isDrawerOpen, setIsDrawerOpen, redemItem, redem_catalouge_id = '' }) => {
    const [items, setItems] = useState([]);
    const [loader, setLoader] = useState(false);
    const { comRequest } = useContext(ComponentRequest)
    const [redemPartner, setRedemPartner] = useState([])

    useEffect(() => {
        redemPartnerUserList('&rlId=12').then((d) => setRedemPartner(d.data))
    }, [])

    const initialValues = { mapping: [], logistic_type: 1, logistic_value: '', sku_tds: '', redem_id: comRequest.redem_id, ...items, redemption_partner_id: "", is_free_product: items?.is_free_product || 'no' };

    /**
     * Handles form submission for redemption mapping.
     * Shows confirmation dialog and calls API.
     */
    const handleSubmit = async (values, actions) => {
        try {
            setLoader(true); // Show loader
            actions.setSubmitting(true);

            const confirmResult = await Swal.fire({
                title: 'Are you sure?',
                text: 'Are you sure you want to update?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: "Confirm",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-secondary'
                }
            });

            if (confirmResult.isConfirmed) {
                values.redem_id = comRequest.redem_id;
                const response = await addRedem(values);
                swlAlertMapping(response);
                if (response.code !== 0) {
                    setTimeout(() => {
                        setIsDrawerOpen(!isDrawerOpen);
                    }, 500);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoader(false); // Hide loader
            actions.setSubmitting(false);
        }
    };
    /**
     * Closes the drawer UI.
     */
    const drawerClose = () => {
        setIsDrawerOpen(!isDrawerOpen)
    }
    const drawerStyles = {
        position: "fixed",
        top: 65,
        right: isDrawerOpen ? 0 : "-100%",
        width: "50%",
        height: '100%',
        maxHeight: "calc(100vh - 65px)",
        overflowY: "auto",
        boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.5)",
        backgroundColor: "#fff",
        transition: "right 0.3s ease",
        zIndex: 1000,
    };

    /**
     * Effect to fetch user types and initialize mapping data when drawer opens.
     */
    useEffect(() => {
        /**
         * Fetches all user types and prepares mapping data for the drawer form.
         */
        const fetchUserTypes = async () => {
            setLoader(true)
            try {
                getUserTypeAll().then((d) => {
                    let dataTyped = []
                    //dataTyped['redem_id'] = comRequest?.redem_id
                    dataTyped['skus'] = redemItem ? redemItem : {};
                    dataTyped['mapping'] = d?.map(x => ({
                        sku_id: dataTyped['skus']?.id,
                        user_type_id: x?.id,
                        user_type_name: x?.type_name,
                        from_date: '',
                        to_date: '',
                        point: '',
                        offer_point: ""
                        // amount: '',
                    })) || []
                    setItems(dataTyped)
                })
            } catch (error) {
                console.error('Error fetching user types:', error);
            } finally {
                setLoader(false)
            }
        }
        fetchUserTypes()
    }, [redemItem])

    /**
     * ProductData component for displaying product details in the drawer.
     */
    const ProductData = ({ input }) => {
        const productFields = [
            { label: "Brand", value: input?.brand_name },
            { label: "Business Segment", value: input?.business_segment_name },
            { label: "Sub Business Segment", value: input?.sub_business_segment_name },
            { label: "Category", value: input?.category_name },
            { label: "Sub Category", value: input?.product_sub_category_name },
            { label: "Product", value: input?.product_name },
            { label: "Sku", value: input?.sku_name },
        ];

        return (
            <div className="p-4 mt-2 rounded overflow-auto" style={{ backgroundColor: '#f6f6f6' }}>
                <div className="row">
                    {productFields.map((field, index) => (
                        <div
                            className="col-12 col-sm-6 col-md-3 mb-2"
                            key={index}
                        >
                            <div className="d-flex align-items-center">
                                <span className="text-gray-600 me-1">{field.label}:</span>
                                <span className="fw-semibold text-dark">{field.value || '-'}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <>
            {items != [] && (
                <div
                    className="card rounded-0"
                    style={drawerStyles}
                >

                    <div className="d-flex justify-content-between align-items-center mt-2 px-2 pb-4">
                        <Formik enableReinitialize initialValues={initialValues} validationSchema={editUserSchema} onSubmit={handleSubmit}>
                            {({ values, handleSubmit, setFieldValue, isSubmitting, handleChange }) => (
                                <form className='w-100' onSubmit={handleSubmit}>
                                    {isSubmitting && <FullPageLoader />}
                                    <div style={{ border: '1px solid #E6E6E6' }} className="card">
                                        <div className="card-header" style={{ minHeight: '39px' }}>
                                            <div className="card-title fw-semibold fs-5 py-1">{redem_catalouge_id ? "Redemption Catalouge" : `Selected SKU `}</div>
                                            <button
                                                onClick={() => drawerClose()}
                                                type="button"
                                                className="btn btn-danger rounded-circle p-0 d-flex align-items-center justify-content-center mt-2 fs-1"
                                                style={{ width: '25px', height: '25px' }}
                                            >
                                                &times;
                                            </button>
                                        </div>
                                        <div style={{ minHeight: '25px' }} className="card-body p-3">
                                            <div className='row'>
                                                <div className='col-md-12'>
                                                    <ProductData input={initialValues?.skus} />
                                                </div>
                                            </div>

                                            <div style={{ background: '#f5e5e5' }} className='p-4 mt-2 rounded'>
                                                <div className='d-flex flex-wrap gap-4'>

                                                    {/* Logistic Type */}
                                                    <div className='d-flex flex-column'>
                                                        <label className="fw-semibold fs-6 required">Logistic type</label>
                                                        <div className='d-flex  gap-3 mt-2'>
                                                            <div className="form-check form-check-inline">
                                                                <Field
                                                                    type='radio'
                                                                    onChange={() => { setFieldValue('logistic_type', 1) }}
                                                                    name='logistic_type'
                                                                    id='inlineenabled'
                                                                    className={clsx('form-check-input')}
                                                                    autoComplete='off'
                                                                    value={1}
                                                                    checked={values.logistic_type === 1}
                                                                />
                                                                <label className="form-check-label" htmlFor="inlineenabled">
                                                                    <span className="fw-semibold fs-6 text-gray-700">Fixed Value</span>
                                                                </label>
                                                            </div>
                                                            <div className="form-check form-check-inline">
                                                                <Field
                                                                    type='radio'
                                                                    onChange={() => { setFieldValue('logistic_type', 2) }}
                                                                    id='inlinedisabled'
                                                                    name='logistic_type'
                                                                    className={clsx('form-check-input')}
                                                                    autoComplete='off'
                                                                    value={2}
                                                                    checked={values.logistic_type === 2}
                                                                />
                                                                <label className="form-check-label" htmlFor="inlinedisabled">
                                                                    <span className="fw-semibold fs-6 text-gray-700">Percentage %</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <ErrorMessage name='logistic_type' component="div" className="text-danger mt-1" />
                                                    </div>

                                                    {/* Logistic Value */}
                                                    <div className='d-flex flex-column'>
                                                        <label className="fw-semibold fs-6 required">
                                                            Logistic {values.logistic_type === 1 ? 'Fixed' : '%'} value
                                                        </label>
                                                        <Field
                                                            name='logistic_value'
                                                            placeholder='value'
                                                            type='number'
                                                            className='form-control rounded-0'
                                                            onChange={handleChange}
                                                        />
                                                        <ErrorMessage name='logistic_value' component="div" className="text-danger mt-1" />
                                                    </div>

                                                    {/* TDS (appeared twice in your original code, so just once here) */}
                                                    <div className='d-flex flex-column' >
                                                        <label className="fw-semibold fs-6 required">TDS</label>
                                                        <Field
                                                            name='sku_tds'
                                                            type='number'
                                                            className='form-control rounded-0'
                                                            onChange={handleChange}
                                                        />
                                                        <ErrorMessage name='sku_tds' component="div" className="text-danger mt-1" />
                                                    </div>
                                                    <div className='d-flex flex-column'>
                                                        <label className="fw-semibold fs-6 required">Redemption Partner</label>
                                                        <Field
                                                            as="select"
                                                            name="redem_partner_name"
                                                            className="form-control form-select rounded-0"
                                                            onChange={handleChange}
                                                        >
                                                            <option value="">IIRPL</option>
                                                            {redemPartner && redemPartner.map((partner) => (
                                                                <option key={partner?.id} value={partner?.id} className="text-capitalize">
                                                                    {partner?.first_name} {partner?.last_name} {partner?.firm_name && `(${partner.firm_name})`}

                                                                </option>
                                                            ))}
                                                        </Field>
                                                        <ErrorMessage name="redem_partner_name" component="div" className="text-danger mt-1" />
                                                    </div>

                                                    {/* Free Product */}
                                                    <div className='d-flex flex-column'>
                                                        <label className="fw-semibold fs-6 required">Is Free Product</label>
                                                        <div className='d-flex gap-3 mt-2'>
                                                            <div className="form-check form-check-inline">
                                                                <Field
                                                                    type='radio'
                                                                    onChange={() => { setFieldValue('is_free_product', 'yes') }}
                                                                    name='is_free_product'
                                                                    id='isFreeProductYes'
                                                                    className={clsx('form-check-input')}
                                                                    autoComplete='off'
                                                                    value='yes'
                                                                    checked={values.is_free_product === 'yes'}
                                                                />
                                                                <label className="form-check-label" htmlFor="isFreeProductYes">
                                                                    <span className="fw-semibold fs-6 text-gray-700">Yes</span>
                                                                </label>
                                                            </div>
                                                            <div className="form-check form-check-inline">
                                                                <Field
                                                                    type='radio'
                                                                    onChange={() => { setFieldValue('is_free_product', 'no') }}
                                                                    id='isFreeProductNo'
                                                                    name='is_free_product'
                                                                    className={clsx('form-check-input')}
                                                                    autoComplete='off'
                                                                    value='no'
                                                                    checked={values.is_free_product === 'no'}
                                                                />
                                                                <label className="form-check-label" htmlFor="isFreeProductNo">
                                                                    <span className="fw-semibold fs-6 text-gray-700">No</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <ErrorMessage name='is_free_product' component="div" className="text-danger mt-1" />
                                                    </div>
                                                </div>
                                            </div>


                                            <FieldArray
                                                name="mapping"
                                                render={arrayHelpers => (
                                                    <div className='w-100'>
                                                        {values && values?.mapping.length > 0 && (
                                                            values.mapping.map((input, index) => (
                                                                <div style={{ background: '#f6f6f6' }} className=' border border-1 p-4 mt-2 rounded' key={index}>
                                                                    <div className='d-flex mb-3 gap-2'>

                                                                        {/* User Type Field */}
                                                                        <div className="col-md-4">
                                                                            <div className="row">
                                                                                <label className="col-lg-4 col-form-label fw-semibold fs-6">User Type</label>
                                                                                <div className="col-md-8 mt-3">
                                                                                    <span className='fw-semibolder'>{input?.user_type_name}</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Points Field */}
                                                                        <div className="col-md-6">
                                                                            <div className="row">
                                                                                <label className="col-lg-4 col-form-label required fw-semibold fs-6">Points</label>
                                                                                <div className="col-md-8">
                                                                                    <Field
                                                                                        name={`mapping.${index}.point`}
                                                                                        placeholder={'Point'}
                                                                                        type="text"
                                                                                        className="form-control rounded-0"
                                                                                        onChange={handleChange}
                                                                                        value={input?.point}
                                                                                    />
                                                                                    <ErrorMessage name={`mapping.${index}.point`} component="div" className="text-danger" />
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                    </div>

                                                                    {/* Special Point Checkbox */}
                                                                    <div className="d-flex mb-3 gap-2">
                                                                        <div className="col-md-12">
                                                                            <div class="form-check form-check-inline">
                                                                                <Field
                                                                                    name={`mapping.${index}.special_point`}
                                                                                    type="checkbox"
                                                                                    className="form-check-input"
                                                                                    onChange={handleChange}
                                                                                    checked={input?.special_point}
                                                                                />
                                                                                <label class="form-check-label" for="inlineenabled"><span class="form-check-label ms-0 fw-semibold fs-6 text-danger">Do you want to add an offer point?</span>
                                                                                </label>
                                                                            </div>

                                                                        </div>
                                                                    </div>

                                                                    {/* Conditionally render fields if Special Point is checked */}
                                                                    {input?.special_point && (
                                                                        <div>
                                                                            {/* Special Point Minute */}
                                                                            <div className="d-flex mb-3 gap-2">
                                                                                <div className="col-md-6">
                                                                                    <div className="row">
                                                                                        <label className="col-lg-4 col-form-label required fw-semibold fs-6">Offer Point</label>
                                                                                        <div className="col-md-8">
                                                                                            <Field
                                                                                                name={`mapping.${index}.offer_point`}
                                                                                                placeholder={'Offer Point'}
                                                                                                type="number"
                                                                                                className="form-control rounded-0"
                                                                                                onChange={handleChange}
                                                                                                value={input?.offer_point}
                                                                                            />
                                                                                            <ErrorMessage name={`mapping.${index}.offer_point`} component="div" className="text-danger" />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            {/* From Date */}
                                                                            <div className="d-flex mb-3 gap-2">
                                                                                <div className="col-md-6">
                                                                                    <div className="row">
                                                                                        <label className="col-lg-4 col-form-label required fw-semibold fs-6">From Date</label>
                                                                                        <div className="col-md-8">
                                                                                            <Field
                                                                                                name={`mapping.${index}.from_date`}
                                                                                                type="date"
                                                                                                className="form-control rounded-0"
                                                                                                onChange={handleChange}
                                                                                                value={input?.from_date}
                                                                                            />
                                                                                            <ErrorMessage name={`mapping.${index}.from_date`} component="div" className="text-danger" />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                {/* To Date */}
                                                                                <div className="col-md-6">
                                                                                    <div className="row">
                                                                                        <label className="col-lg-4 col-form-label required fw-semibold fs-6">To Date</label>
                                                                                        <div className="col-md-8">
                                                                                            <Field
                                                                                                name={`mapping.${index}.to_date`}
                                                                                                type="date"
                                                                                                className="form-control rounded-0"
                                                                                                onChange={handleChange}
                                                                                                value={input?.to_date}
                                                                                            />
                                                                                            <ErrorMessage name={`mapping.${index}.to_date`} component="div" className="text-danger" />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                        {/* <div className="card-body p-3 pt-3">
                                            <div style={{ background: '#f1f1f1', minHeight: '25px' }} className="card mt-4">
                                                <div style={{ minHeight: '25px', background: '#f5e5e5' }} className="card-header h-min-35px">
                                                    <div className="card-title fs-5"> Mapping</div>
                                                </div>
                                               
                                            </div>
                                        </div> */}
                                        <div className="card-footer py-2">
                                            <div className="d-flex gap-4 justify-content-end">
                                                <button type="button" onClick={() => drawerClose()} id="kt_cancel" className="btn btn-dark">
                                                    <span className="indicator-label">Cancel</span>
                                                </button>
                                                <button type="submit" id="kt_ecommerce_add_brand_submit" className="btn btn-primary">
                                                    <span className="indicator-label">{redem_catalouge_id ? 'Update ' : 'Save '}</span>
                                                    {isSubmitting && (
                                                        <span className="indicator-progress">
                                                            Please wait...
                                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                        </span>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </Formik>
                    </div>
                    {loader &&
                        <FullPageLoader></FullPageLoader>}
                </div>

            )}

        </>
    );
};




export { RightRedemLabel }